import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import PrimaryButton from "../../PrimaryButton";
import { useAuth } from "../../auth/AuthContext";
import Footer from "../../pages/home/Footer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useNavigate } from "react-router-dom";

const Project = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getData } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [background, setBackground] = useState({});
  const [relatedProjects, setRelatedProjects] = useState([]);

  useEffect(() => {
    async function fetchProjectData() {
      try {
        setLoading(true);

        // Get UUID from URL query parameters
        const queryParams = new URLSearchParams(location.search);
        const uuid = queryParams.get("uuid");

        if (!uuid) {
          setError("Project ID not found in URL");
          setLoading(false);
          return;
        }

        // Fetch the specific project
        const response = await getData("proccess.php", `projects_V2`);

        if (response.success && response.data) {
          const filteredProject = response.data.find((p) => p.id == uuid);

          if (filteredProject) {
            setProject(filteredProject);
          }

          // Fetch background for donation page
          const bgResponse = await getData("proccess.php", "backgrounds");
          if (bgResponse.success) {
            const contactbg = bgResponse.data.find(
              (bg) => bg.page_name === "donation"
            );
            if (contactbg) {
              setBackground(contactbg);
            }
          }

          // Fetch related projects (same type)
          const allProjectsResponse = await getData(
            "proccess.php",
            "projects_V2"
          );
          if (allProjectsResponse.success) {
            const related = allProjectsResponse.data
              .filter(
                (p) =>
                  p.id !== response.data.id &&
                  p.project_type === response.data.project_type
              )
              .slice(0, 3); // Limit to 3 related projects
            setRelatedProjects(related);
          }
        } else {
          setError(response.message || "Project not found");
        }
      } catch (error) {
        setError(error.message || "Failed to load project data");
      } finally {
        setLoading(false);
      }
    }

    fetchProjectData();
  }, [location.search, getData]);

  const handleDonateClick = () => {
    if (project) {
      navigate(
        `/general-support?projectId=${
          project.id
        }&projectName=${encodeURIComponent(project.name || project.title)}`
      );
    } else {
      navigate("/general-support");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        px={3}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/donations")}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography variant="h5" color="textSecondary" gutterBottom>
          Project not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/donations")}
          sx={{ mt: 2 }}
        >
          View All Projects
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingTop="4rem"
        sx={{ backgroundColor: background.color || "#e7f1f5" }}
      >
        {/* Project Hero Section */}
        <Box width="100%" maxWidth="1200px" px={3} py={4}>
          <Grid container spacing={4}>
            {/* Project Image */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  height: "100%",
                  minHeight: 400,
                }}
              >
                {project.image ? (
                  <img
                    src={`https://mindsinaction.com.na/assets/media/pictures/${project.image}`}
                    alt={project.name || project.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    bgcolor="grey.100"
                  >
                    <Typography color="textSecondary">
                      No Image Available
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Project Details */}
            <Grid item xs={12} md={6}>
              <Box mb={3}>
                <h1 className="primary-header">
                  {project.name || project.title}
                </h1>

                <Divider sx={{ my: 2 }} />

                {/* Project Metadata */}
                <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                  {project.date && (
                    <Box display="flex" alignItems="center">
                      <CalendarTodayIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        {new Date(project.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}

                  {project.beneficiaries && (
                    <Box display="flex" alignItems="center">
                      <PeopleIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        {project.beneficiaries} beneficiaries
                      </Typography>
                    </Box>
                  )}

                  {project.budget && (
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        N$ {parseInt(project.budget).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Donation Button */}

                <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                  Your support will directly support this project and its
                  beneficiaries.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Detailed Description Section */}
        <Box
          width="100%"
          px={3}
          py={6}
          sx={{ backgroundColor: "white" }}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Box width={"100%"} maxWidth={"1200px"}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Project Overview
              </Typography>

              {project.description ? (
                <Box>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}
                  >
                    {project.description}
                  </Typography>
                </Box>
              ) : (
                <Typography color="textSecondary" paragraph>
                  No detailed description available for this project.
                </Typography>
              )}
            </Grid>
          </Box>
        </Box>

        {/* Related Projects */}
        {relatedProjects.length > 0 && (
          <Box
            width="100%"
            maxWidth="1200px"
            px={3}
            py={6}
            sx={{ backgroundColor: "#e7f1f5" }}
          >
            <Typography variant="h4" fontWeight="bold" gutterBottom mb={4}>
              Other Projects You Might Like
            </Typography>

            <Grid container spacing={3}>
              {relatedProjects.map((relatedProject) => (
                <Grid item xs={12} sm={6} md={4} key={relatedProject.id}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "transform 0.3s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    {relatedProject.image && (
                      <Box height="200px" overflow="hidden">
                        <img
                          src={`https://mindsinaction.com.na/assets/media/pictures/${relatedProject.image}`}
                          alt={relatedProject.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    )}

                    <Box p={3}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom
                        noWrap
                      >
                        {relatedProject.name || relatedProject.title}
                      </Typography>

                      {relatedProject.tagline && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          paragraph
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {relatedProject.tagline}
                        </Typography>
                      )}

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/donation/project/?uuid=${relatedProject.id}`
                          )
                        }
                        sx={{ mt: 2 }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Call to Action */}
        <Box width="100%" maxWidth="1200px" px={3} py={6} textAlign="center">
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Ready to Make a Difference?
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Your donation will directly impact lives and contribute to
            sustainable community development.
          </Typography>

          <Box
            width={'100%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'} 
            marginTop={'2rem'}
          ><PrimaryButton
            link={`/project-support?projectId=${
              project.id
            }&projectName=${encodeURIComponent(project.name || project.title)}`}
            bcolor="#90ca25"
            tcolor="#fff"
            btnwidth="200px"
            tittle={`support Project`}
          /></Box>
          
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default Project;
